import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Offcanvas, Popover } from 'bootstrap';
import { getInformation, Information } from '../model/InformationService';
import { t } from '../i18n';

const Information = ({ id, content, showMore = false, className }: Props) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const offcanvasRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<Popover | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout>();
  const idRef = useRef(`information-${id}-${Date.now()}`);
  const informationRef = useRef<Information>({ id: '', name: '', tooltip: '', moreInformation: ''});

  const clearHideTimeout = () => {
    hideTimeoutRef.current && clearTimeout(hideTimeoutRef.current);
  }

  const hidePopover = () => {
    hideTimeoutRef.current = setTimeout(() => {
      popoverRef.current?.hide();
    }, 200);
  }

  const showOffcanvas = () => {
    if(!offcanvasRef.current) {
      return;
    }
    popoverRef.current?.hide();
    Offcanvas.getOrCreateInstance(offcanvasRef.current).show();
  }

  const getPopoverContent = () => {
    const content = document.createElement('div');
    content.className = 'information-popover-content';
    const popoverTooltip = document.createElement('div');
    popoverTooltip.className = 'information-popover-title';
    popoverTooltip.textContent = informationRef.current.tooltip;
    content.appendChild(popoverTooltip);

    if(showMore) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn p-0 mt-2';
      button.textContent = t('common.showMore');
      button.addEventListener('click', showOffcanvas);
      content.appendChild(button);
    }

    return content;
  }

  useEffect(() => {
    if(!triggerRef.current) {
      return;
    }
    informationRef.current = getInformation(id) || { id: '', name: '', tooltip: '', moreInformation: '' };
    const trigger = triggerRef.current;
    const popover = new Popover(trigger, {
      html: true,
      sanitize: false,
      trigger: 'manual',
      placement: 'top',
      container: 'body',
      content: getPopoverContent
    });
    popoverRef.current = popover;
    const onPopoverShown = () => {
      const tip = (popover as any).getTipElement ? (popover as any).getTipElement() : (popover as any).tip;
      tip?.addEventListener('mouseenter', clearHideTimeout);
      tip?.addEventListener('mouseleave', hidePopover);
    }
    trigger.addEventListener('shown.bs.popover', onPopoverShown);
    return () => {
      clearHideTimeout();
      trigger.removeEventListener('shown.bs.popover', onPopoverShown);
      popover.dispose();
      popoverRef.current = null;
    }
  }, [ id, showMore ]);

  return (
    <>
      <span ref={triggerRef} className={className} role="button" tabIndex={0} aria-label={informationRef.current.tooltip} onMouseEnter={() => { clearHideTimeout(); popoverRef.current?.show(); }} onMouseLeave={hidePopover} onFocus={() => popoverRef.current?.show()} onBlur={hidePopover}>
        {
          content ? content :
            <svg className="information-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 19 18" fill="none" aria-hidden="true">
              <path d="M9.4317 17.1974C13.9045 17.1974 17.5304 13.5715 17.5304 9.09869C17.5304 4.62591 13.9045 1 9.4317 1C4.95892 1 1.33301 4.62591 1.33301 9.09869C1.33301 13.5715 4.95892 17.1974 9.4317 17.1974Z" stroke="#E1E1E3" strokeWidth="1.61974" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.43164 12.3381V9.09863" stroke="#E1E1E3" strokeWidth="1.61974" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.43164 5.85938H9.44039" stroke="#E1E1E3" strokeWidth="1.61974" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        }
      </span>
      {
        showMore && createPortal(
          <div ref={offcanvasRef} className="offcanvas offcanvas-end" tabIndex={-1} id={idRef.current} aria-labelledby={`${idRef.current}-label`}>
            <div className="offcanvas-header">
              <h3 id={`${idRef.current}-label`} className="offcanvas-title">{informationRef.current.name}</h3>
              <button type="button" className="btn-close bg-white" data-bs-dismiss="offcanvas" aria-label={t('common.close')}></button>
            </div>
            <div className="offcanvas-body">
              <h5 className="mt-4">{informationRef.current.tooltip}</h5>
              <p className="mb-0 mt-4">{informationRef.current.moreInformation}</p>
            </div>
          </div>,
          document.body
        )
      }
    </>
  );
}

interface Props {
  id: string,
  content?: ReactNode,
  showMore?: boolean,
  className?: string
}

export { Information };
